import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";

import MDTypography from "../../../../components/MDTypography";
import { sellerboardApi } from "../../../../services/api/plenty/sellerboard";

const StockSellerboardReports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Sellerboard Reports states
  const [sellerboardReports, setSellerboardReports] = useState([]);
  const [sellerboardLoading, setSellerboardLoading] = useState(false);
  const [sellerboardDialog, setSellerboardDialog] = useState(false);
  const [sellerboardEditMode, setSellerboardEditMode] = useState(false);
  const [sellerboardEditingId, setSellerboardEditingId] = useState(null);
  const [sellerboardForm, setSellerboardForm] = useState({
    report_type: "",
    link: "",
  });
  const [sellerboardError, setSellerboardError] = useState("");
  const [sellerboardSuccess, setSellerboardSuccess] = useState("");
  const [processingReportId, setProcessingReportId] = useState(null);

  // Helper function for error messages
  const getErrorMessage = (error) => {
    if (error.response) {
      return (
        error.response.data?.detail ||
        error.response.data?.message ||
        "Server error occurred"
      );
    }
    return error.message || "An unexpected error occurred";
  };

  // Sellerboard Reports handlers
  const loadSellerboardReports = useCallback(async () => {
    console.log("Loading sellerboard reports...");
    setSellerboardLoading(true);
    setSellerboardError("");

    try {
      const response = await sellerboardApi.get_all_reports();
      console.log("Sellerboard reports loaded:", response.data);
      setSellerboardReports(response.data);
    } catch (error) {
      console.error("Failed to load sellerboard reports:", error);
      setSellerboardError(getErrorMessage(error));
    } finally {
      setSellerboardLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSellerboardReports();
  }, []);

  const handleOpenSellerboardDialog = (report = null) => {
    if (report) {
      setSellerboardEditMode(true);
      setSellerboardEditingId(report.id);
      setSellerboardForm({
        report_type: report.report_type,
        link: report.link,
      });
    } else {
      setSellerboardEditMode(false);
      setSellerboardEditingId(null);
      setSellerboardForm({
        report_type: "",
        link: "",
      });
    }
    setSellerboardDialog(true);
  };

  const handleCloseSellerboardDialog = () => {
    setSellerboardDialog(false);
    setSellerboardEditMode(false);
    setSellerboardEditingId(null);
    setSellerboardForm({
      report_type: "",
      link: "",
    });
    setSellerboardError("");
  };

  const handleSellerboardFormChange = (field, value) => {
    setSellerboardForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSellerboardReport = async () => {
    setSellerboardLoading(true);
    setSellerboardError("");

    try {
      if (sellerboardEditMode) {
        await sellerboardApi.update_report(
          sellerboardEditingId,
          sellerboardForm,
        );
        setSellerboardSuccess("Report updated successfully!");
      } else {
        await sellerboardApi.create_report(sellerboardForm);
        setSellerboardSuccess("Report created successfully!");
      }

      await loadSellerboardReports();
      handleCloseSellerboardDialog();
      setTimeout(() => setSellerboardSuccess(""), 3000);
    } catch (error) {
      console.error("Failed to save sellerboard report:", error);
      setSellerboardError(getErrorMessage(error));
    } finally {
      setSellerboardLoading(false);
    }
  };

  const handleDeleteSellerboardReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }

    setSellerboardLoading(true);
    setSellerboardError("");

    try {
      await sellerboardApi.delete_report(reportId);
      setSellerboardSuccess("Report deleted successfully!");
      await loadSellerboardReports();
      setTimeout(() => setSellerboardSuccess(""), 3000);
    } catch (error) {
      console.error("Failed to delete sellerboard report:", error);
      setSellerboardError(getErrorMessage(error));
    } finally {
      setSellerboardLoading(false);
    }
  };

  const handleDownloadSellerboardReport = async (reportType) => {
    try {
      const response = await sellerboardApi.download_report(reportType);
      // The download happens in the browser via redirect or file download
      window.open(response.data.download_url || response.data.link, "_blank");
    } catch (error) {
      console.error("Failed to download sellerboard report:", error);
      setSellerboardError(getErrorMessage(error));
    }
  };

  return (
    <>
      <Box>
        {sellerboardError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sellerboardError}
          </Alert>
        )}
        {sellerboardSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {sellerboardSuccess}
          </Alert>
        )}

        {/* Information Box - How to Connect Sellerboard */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            How to Connect Sellerboard Reports:
          </Typography>
          <ol
            style={{
              margin: 0,
              paddingLeft: "20px",
              marginBottom: "8px",
            }}
          >
            <li>
              <Typography variant="body2">
                Open Sellerboard and navigate to{" "}
                <a
                  href="https://app.sellerboard.com/de/settings/automation"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  Settings → Automation
                </a>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Click on "Add" to create a new automated report
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Select the report type (Dashboard, Bestellungen, Lager, etc.)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Set format to <strong>CSV</strong> and delivery method to{" "}
                <strong>"Send report per link"</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Save the configuration and copy the generated link
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Use the "Add Report" button below to paste the link and select
                the matching report type
              </Typography>
            </li>
          </ol>
        </Alert>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <MDTypography variant="h6" fontWeight="medium">
            Sellerboard Reports Management
          </MDTypography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSellerboardReports}
              disabled={sellerboardLoading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenSellerboardDialog()}
              disabled={sellerboardLoading}
            >
              Add Report
            </Button>
          </Box>
        </Box>

        {sellerboardLoading && sellerboardReports.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : sellerboardReports.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No Sellerboard reports found. Click "Add Report" to create one.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Report Type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Link</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Created At</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sellerboardReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <Chip
                        label={report.report_type}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {report.link}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(report.created_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleDownloadSellerboardReport(report.report_type)
                          }
                          title="Download Report"
                        >
                          <DescriptionIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleOpenSellerboardDialog(report)}
                          title="Edit Report"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            handleDeleteSellerboardReport(report.id)
                          }
                          title="Delete Report"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Sellerboard Report Dialog */}
      <Dialog
        open={sellerboardDialog}
        onClose={handleCloseSellerboardDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {sellerboardEditMode
            ? "Edit Sellerboard Report"
            : "Add Sellerboard Report"}
        </DialogTitle>
        <DialogContent>
          {sellerboardError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sellerboardError}
            </Alert>
          )}

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={sellerboardForm.report_type}
              onChange={(e) =>
                handleSellerboardFormChange("report_type", e.target.value)
              }
              label="Report Type"
            >
              <MenuItem
                value="Dashboard"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some((r) => r.report_type === "Dashboard")
                }
              >
                Dashboard
              </MenuItem>
              <MenuItem
                value="Bestellungen"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some(
                    (r) => r.report_type === "Bestellungen",
                  )
                }
              >
                Bestellungen
              </MenuItem>
              <MenuItem
                value="Umsatz nach Produkt/Monat"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some(
                    (r) => r.report_type === "Umsatz nach Produkt/Monat",
                  )
                }
              >
                Umsatz nach Produkt/Monat
              </MenuItem>
              <MenuItem
                value="Wiederkehrende Kunden"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some(
                    (r) => r.report_type === "Wiederkehrende Kunden",
                  )
                }
              >
                Wiederkehrende Kunden
              </MenuItem>
              <MenuItem
                value="Änderung der FBA Gebühren"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some(
                    (r) => r.report_type === "Änderung der FBA Gebühren",
                  )
                }
              >
                Änderung der FBA Gebühren
              </MenuItem>
              <MenuItem
                value="Lager"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some((r) => r.report_type === "Lager")
                }
              >
                Lager
              </MenuItem>
              <MenuItem
                value="Einkaufspreise"
                disabled={
                  !sellerboardEditMode &&
                  sellerboardReports.some(
                    (r) => r.report_type === "Einkaufspreise",
                  )
                }
              >
                Einkaufspreise
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Report Link"
            value={sellerboardForm.link}
            onChange={(e) =>
              handleSellerboardFormChange("link", e.target.value)
            }
            margin="normal"
            required
            helperText="Direct link to the Sellerboard report CSV file"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSellerboardDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSellerboardReport}
            disabled={
              !sellerboardForm.report_type ||
              !sellerboardForm.link ||
              sellerboardLoading
            }
            startIcon={
              sellerboardLoading ? <CircularProgress size={20} /> : <SaveIcon />
            }
          >
            {sellerboardLoading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StockSellerboardReports;
